import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Tooltip, 
  message, 
  Modal,
  Input, 
  Select, 
  Row,
  Col,
  Avatar,
  Skeleton,
  Typography
} from 'antd';
import customerService from '../../services/customerService';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined, 
  WarningOutlined, 
  StopOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getLicenses, 
  deleteLicense,
  LICENSE_STATUS,
  LICENSE_TYPES
} from '../../services/licenseService';

const { Search } = Input;
const { Option } = Select;

// Reuse the CustomerCell component from LicenseList
const CustomerCell = ({ customerLicenses }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerLicenses || customerLicenses.length === 0) {
        return;
      }

      const primaryCustomer = customerLicenses.find(cl => cl.is_primary) || customerLicenses[0];
      const customerId = primaryCustomer?.customer_id;

      if (!customerId) {
        setError('No customer ID found');
        return;
      }

      if (primaryCustomer.company_name) {
        setCustomer(primaryCustomer.company_name);
        return;
      }

      setLoading(true);
      try {
        const customerData = await customerService.getCustomer(customerId);
        setCustomer(customerData);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError('Failed to load customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerLicenses]);

  if (loading) return <Skeleton.Input style={{ width: 150 }} active size="small" />;
  if (error) return <span>Error loading customer</span>;
  if (!customerLicenses || customerLicenses.length === 0) {
    return <span>Unassigned</span>;
  }

  const primaryCustomer = customerLicenses.find(cl => cl.is_primary) || customerLicenses[0];
  
  if (customer) {
    const displayName = customer.company_name || customer.contact_name || 'Unnamed Customer';
    const logoUrl = customer.company_logo ? (
      customer.company_logo.startsWith('uploads/') ? 
        `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}/static/${customer.company_logo.replace('uploads/', '')}` :
        customer.company_logo.startsWith('/static/') ?
          `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}${customer.company_logo}` :
          customer.company_logo
    ) : null;

    return (
      <Space>
        {logoUrl && (
          <Avatar 
            src={logoUrl} 
            shape="square" 
            size="small"
            style={{ marginRight: 8 }}
          />
        )}
        {displayName}
      </Space>
    );
  }

  return (
    <span>
      {primaryCustomer.company_name || primaryCustomer.customer_name || 'Unnamed Customer'}
    </span>
  );
};

const RevokedLicensesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if user has permission to perform an action
  const hasPermission = (permission) => {
    if (!user) return false;
    // For now, only check if user is admin
    // You can extend this to check specific permissions from user.role or user.permissions
    return user.role?.toLowerCase() === 'admin';
  };
  
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: LICENSE_STATUS.REVOKED,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchLicenses = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const { search, ...restFilters } = filters;
      
      // Always ensure we're filtering for revoked status
      const status = LICENSE_STATUS.REVOKED;
      
      const queryParams = {
        skip: ((params.page || pagination.page) - 1) * (params.limit || pagination.limit),
        limit: params.limit || pagination.limit,
        search: search || undefined,
        status: status, // Explicitly set the status
        ...(params.filters || {})
      };

      console.log('Fetching licenses with params:', queryParams);
      const response = await getLicenses(
        queryParams.skip,
        queryParams.limit,
        queryParams.status,
        undefined, // type
        undefined, // customerId
        undefined, // expiresSoon
        queryParams.search
      );
      
      const licenses = Array.isArray(response.items) ? response.items : [];
      console.log('License API response:', licenses);
      setLicenses(licenses);
      
      // Update pagination
      setPagination(prev => ({
        ...prev,
        page: params.page !== undefined ? params.page : prev.page,
        limit: params.limit !== undefined ? params.limit : prev.limit,
        total: response?.total || response?.items?.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching revoked licenses:', error);
      message.error('Failed to load revoked licenses');
      // Reset to empty array in case of error to prevent showing stale data
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    const abortController = new AbortController();
    
    fetchLicenses();
    
    return () => {
      abortController.abort();
    };
  }, [fetchLicenses]);

  const handleTableChange = (pagination, filters, sorter) => {
    fetchLicenses({
      page: pagination.current,
      limit: pagination.pageSize,
      filters: {
        ...filters,
        // Don't override status in filters to avoid conflicts
      },
      ...(sorter.field ? { sortField: sorter.field, sortOrder: sorter.order } : {}),
    });
  };

  const handleSearch = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleDelete = async () => {
    if (!licenseToDelete) return;
    
    try {
      setDeleting(true);
      await deleteLicense(licenseToDelete);
      message.success('License deleted successfully');
      fetchLicenses();
    } catch (error) {
      console.error('Error deleting license:', error);
      message.error(error.response?.data?.message || 'Failed to delete license');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setLicenseToDelete(null);
    }
  };

  const columns = [
    {
      title: 'License Key',
      dataIndex: 'key',
      key: 'key',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/licenses/${record.customer_licenses[0].license_id}`)}
          style={{ padding: 0 }}
        >
          <code>{record.license_key}</code>
        </Button>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer_licenses',
      key: 'customer',
      render: (customerLicenses) => (
        <CustomerCell customerLicenses={customerLicenses} />
      ),
    },
    {
      title: 'Type',
      dataIndex: 'license_type',
      key: 'license_type',
      render: (type) => {
        const typeInfo = LICENSE_TYPES[type] || { label: type, color: 'default' };
        return (
          <Tag color={typeInfo.color}>
            {typeInfo.label || type}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let icon = null;
        let color = 'default';
        
        switch (status) {
          case LICENSE_STATUS.ACTIVE:
            icon = <CheckCircleOutlined />;
            color = 'green';
            break;
          case LICENSE_STATUS.EXPIRED:
            icon = <StopOutlined />;
            color = 'red';
            break;
          case LICENSE_STATUS.REVOKED:
            icon = <StopOutlined />;
            color = 'red';
            break;
          case LICENSE_STATUS.PENDING:
            icon = <ClockCircleOutlined />;
            color = 'blue';
            break;
          case LICENSE_STATUS.SUSPENDED:
            icon = <PauseCircleOutlined />;
            color = 'orange';
            break;
          case LICENSE_STATUS.IN_GRACE_PERIOD:
            icon = <PauseCircleOutlined />;
            color = 'orange';
            break;
          default:
            break;
        }
        
        return (
          <Tag color={color} icon={icon}>
            {status}
          </Tag>
        );
      },
    }
  ];

  return (
    <div className="license-list">
      <Card 
        title="Revoked Licenses"
        extra={
          user.role !== 'USER' && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => navigate('/admin/licenses/create')}
            >
              New License
            </Button>
          )
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="Search licenses..."
              allowClear
              enterButton={<SearchOutlined style={{ paddingTop: '8px' }}/>}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by type"
              allowClear
              onChange={(value) => handleFilterChange('license_type', value)}
            >
              <Option value={LICENSE_TYPES.SUBSCRIPTION}>Subscription</Option>
              <Option value={LICENSE_TYPES.PERPETUAL}>Perpetual</Option>
            </Select>
          </Col>
        </Row>

        <Modal
          title="Delete License"
          open={showDeleteModal}
          onOk={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <p>Are you sure you want to delete this license? This action cannot be undone.</p>
        </Modal>

        <Table
          columns={columns}
          dataSource={licenses}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} licenses`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default RevokedLicensesList;
